import { useState, useEffect } from "react";
import { AccountLayout } from "../components/AccountLayout";
import { fetchProfile, addAddress, updateAddress, deleteAddress } from "../api";
import { getStoredAuthToken } from "../authStorage";

export default function AddressesPage({ user, onLogout }) {
    const [addresses, setAddresses] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", fullName: "", address: "", city: "", state: "", pincode: "", phone: "" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadAddresses = async () => {
            try {
                const token = getStoredAuthToken();
                if (token) {
                    const profile = await fetchProfile(token);
                    if (profile.addresses) setAddresses(profile.addresses);
                }
            } catch (error) {
                console.error("Failed to load addresses:", error);
            } finally {
                setLoading(false);
            }
        };
        loadAddresses();
    }, []);

    const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
    const resetForm = () => { setForm({ name: "", fullName: "", address: "", city: "", state: "", pincode: "", phone: "" }); setEditing(null); setShowForm(false); };

    const handleSave = async e => {
        e.preventDefault();
        if (!/^\d{10}$/.test(String(form.phone).trim())) {
            alert("Please enter a valid 10-digit phone number.");
            return;
        }
        if (!/^\d{6}$/.test(String(form.pincode).trim())) {
            alert("Please enter a valid 6-digit pincode.");
            return;
        }

        try {
            const token = getStoredAuthToken();
            if (!token) return;
            const payload = { ...form, street: form.address, zipCode: form.pincode };
            const updatedList = editing
                ? await updateAddress(token, editing, payload)
                : await addAddress(token, payload);
            setAddresses(updatedList);
            resetForm();
        } catch (error) {
            alert(error.message || "Failed to save address");
        }
    };

    const handleEdit = addr => {
        setForm({ name: addr.name || "", fullName: addr.fullName || "", address: addr.street || addr.address || "", city: addr.city || "", state: addr.state || "", pincode: addr.zipCode || addr.pincode || "", phone: addr.phone || "" });
        setEditing(addr._id || addr.id);
        setShowForm(true);
    };

    const handleDelete = async id => {
        try {
            const token = getStoredAuthToken();
            if (!token) return;
            setAddresses(await deleteAddress(token, id));
        } catch { alert("Failed to delete address"); }
    };

    const handleDefault = async id => {
        try {
            const token = getStoredAuthToken();
            if (!token) return;
            setAddresses(await updateAddress(token, id, { isDefault: true }));
        } catch { alert("Failed to set default address"); }
    };

    const inputStyle = {
        width: "100%", padding: "13px 16px", borderRadius: 12,
        border: "1.5px solid #f0d0c8", background: "#fff8f8",
        fontFamily: "'Nunito Sans',sans-serif", fontSize: 14,
        outline: "none", color: "#1a0a08", transition: "border-color 0.2s"
    };
    const focusRed = e => e.target.style.borderColor = "#C0392B";
    const blurRed = e => e.target.style.borderColor = "#f0d0c8";

    return (
        <AccountLayout user={user} onLogout={onLogout}>
            <div className="addresses-page" style={{ animation: "fadeUp 0.4s both" }}>
                <style>{`
                    .address-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 28px;
                    }
                    .address-form-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 14px;
                        margin-bottom: 18px;
                    }
                    .address-cards-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    }
                    @media (max-width: 768px) {
                        .address-header {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            gap: 16px !important;
                        }
                        .address-form-grid {
                            grid-template-columns: 1fr !important;
                        }
                        .address-cards-grid {
                            grid-template-columns: 1fr !important;
                        }
                    }
                `}</style>
                {/* Header */}
                <div className="address-header">
                    <div>
                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 800, color: "#1a0a08", marginBottom: 4 }}>My Addresses</h2>
                        <p style={{ fontSize: 13, color: "#b07878" }}>Manage your delivery addresses.</p>
                    </div>
                    {!showForm && (
                        <button
                            onClick={() => setShowForm(true)}
                            style={{
                                padding: "10px 22px", fontSize: 13, fontWeight: 700,
                                background: "linear-gradient(135deg, #C0392B, #96221A)",
                                color: "white", border: "none", borderRadius: 10,
                                cursor: "pointer", fontFamily: "'Nunito Sans',sans-serif",
                                boxShadow: "0 3px 12px rgba(192,57,43,0.3)",
                                transition: "all 0.2s"
                            }}
                        >+ Add Address</button>
                    )}
                </div>

                {/* Add / Edit form */}
                {showForm && (
                    <div style={{
                        background: "white", borderRadius: 18,
                        border: "1.5px solid #C0392B",
                        padding: 28, marginBottom: 24,
                        animation: "fadeUp 0.3s both",
                        boxShadow: "0 4px 18px rgba(192,57,43,0.1)",
                        overflow: "hidden", position: "relative"
                    }}>
                        <div style={{ height: 3, background: "linear-gradient(90deg, #C0392B, #E8523A)", margin: "-28px -28px 24px" }} />
                        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, marginBottom: 20, color: "#1a0a08" }}>
                            {editing ? "Edit Address" : "New Address"}
                        </h3>
                        <form onSubmit={handleSave}>
                            <div className="address-form-grid">
                                {[["name", "Label (e.g. Home, Office)"], ["fullName", "Full Name"], ["phone", "Phone Number"], ["pincode", "Pin Code"], ["city", "City"], ["state", "State"]].map(([k, label]) => (
                                    <div key={k}>
                                        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#7a5550", marginBottom: 6 }}>{label}</label>
                                        <input required value={form[k]} onChange={upd(k)} placeholder={label} style={inputStyle} onFocus={focusRed} onBlur={blurRed} />
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#7a5550", marginBottom: 6 }}>Full Address</label>
                                <textarea
                                    required value={form.address} onChange={upd("address")}
                                    placeholder="Street address, area, landmark" rows={2}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    onFocus={focusRed} onBlur={blurRed}
                                />
                            </div>
                            <div style={{ display: "flex", gap: 12 }}>
                                <button
                                    type="submit"
                                    style={{
                                        padding: "12px 28px", fontSize: 13, fontWeight: 700,
                                        background: "linear-gradient(135deg, #C0392B, #96221A)",
                                        color: "white", border: "none", borderRadius: 10,
                                        cursor: "pointer", fontFamily: "'Nunito Sans',sans-serif",
                                        boxShadow: "0 3px 12px rgba(192,57,43,0.28)"
                                    }}
                                >{editing ? "Update" : "Save"} Address</button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    style={{
                                        padding: "12px 20px", fontSize: 13, fontWeight: 600,
                                        background: "white", border: "1.5px solid #f0d0c8",
                                        color: "#8B1A1A", borderRadius: 10, cursor: "pointer",
                                        fontFamily: "'Nunito Sans',sans-serif"
                                    }}
                                >Cancel</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Address cards */}
                {loading ? (
                    <div style={{ padding: 40, textAlign: "center", color: "#b07878" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid #C0392B", borderTopColor: "transparent", display: "inline-block", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
                        <div>Loading addresses...</div>
                    </div>
                ) : addresses.length === 0 && !showForm ? (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#b07878" }}>
                        <div style={{ fontSize: 48, marginBottom: 14 }}>📍</div>
                        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#1a0a08", marginBottom: 6 }}>No addresses yet</h3>
                        <p style={{ fontSize: 13 }}>Add a delivery address to get started.</p>
                    </div>
                ) : (
                    <div className="address-cards-grid">
                        {addresses.map(addr => (
                            <div
                                key={addr._id || addr.id}
                                style={{
                                    background: "white", borderRadius: 18,
                                    border: `1.5px solid ${addr.isDefault ? "#C0392B" : "#f0d0c8"}`,
                                    padding: 24, position: "relative",
                                    transition: "all 0.2s",
                                    boxShadow: addr.isDefault ? "0 4px 16px rgba(192,57,43,0.14)" : "0 2px 8px rgba(192,57,43,0.05)"
                                }}
                                onMouseEnter={e => { if (!addr.isDefault) { e.currentTarget.style.borderColor = "#e8b0a8"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(192,57,43,0.1)"; } }}
                                onMouseLeave={e => { if (!addr.isDefault) { e.currentTarget.style.borderColor = "#f0d0c8"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(192,57,43,0.05)"; } }}
                            >
                                {/* Default badge strip */}
                                {addr.isDefault && (
                                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, #C0392B, #E8523A)", borderRadius: "4px 4px 0 0" }} />
                                )}

                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 17 }}>📍</span>
                                        <span style={{ fontWeight: 800, fontSize: 14, color: "#1a0a08" }}>{addr.name || "Address"}</span>
                                        {addr.isDefault && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 50,
                                                background: "linear-gradient(135deg, #C0392B, #96221A)", color: "white",
                                                boxShadow: "0 2px 6px rgba(192,57,43,0.3)"
                                            }}>DEFAULT</span>
                                        )}
                                    </div>
                                </div>

                                <div style={{ fontSize: 13, color: "#7a5550", lineHeight: 1.75, marginBottom: 16 }}>
                                    {addr.fullName}<br />
                                    {addr.street || addr.address}<br />
                                    {addr.city}, {addr.state} – {addr.zipCode || addr.pincode}<br />
                                    {addr.phone && <span style={{ color: "#b07878" }}>📞 {addr.phone}</span>}
                                </div>

                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <button
                                        onClick={() => handleEdit(addr)}
                                        style={{
                                            background: "white", border: "1.5px solid #f0d0c8",
                                            borderRadius: 8, padding: "6px 14px", fontSize: 12,
                                            fontWeight: 600, cursor: "pointer", color: "#8B1A1A",
                                            fontFamily: "'Nunito Sans',sans-serif", transition: "all 0.15s"
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.background = "#fff0f0"; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = "#f0d0c8"; e.currentTarget.style.background = "white"; }}
                                    >Edit</button>

                                    {!addr.isDefault && (
                                        <button
                                            onClick={() => handleDefault(addr._id || addr.id)}
                                            style={{
                                                background: "white", border: "1.5px solid #f0d0c8",
                                                borderRadius: 8, padding: "6px 14px", fontSize: 12,
                                                fontWeight: 600, cursor: "pointer", color: "#C0392B",
                                                fontFamily: "'Nunito Sans',sans-serif", transition: "all 0.15s"
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = "#fff0f0"; e.currentTarget.style.borderColor = "#C0392B"; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#f0d0c8"; }}
                                        >Set Default</button>
                                    )}

                                    {!addr.isDefault && (
                                        <button
                                            onClick={() => handleDelete(addr._id || addr.id)}
                                            style={{
                                                background: "white", border: "1.5px solid #fae8e8",
                                                borderRadius: 8, padding: "6px 14px", fontSize: 12,
                                                fontWeight: 600, cursor: "pointer", color: "#c07070",
                                                fontFamily: "'Nunito Sans',sans-serif", transition: "all 0.15s"
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = "#fff0f0"; e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.color = "#C0392B"; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#fae8e8"; e.currentTarget.style.color = "#c07070"; }}
                                        >Delete</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AccountLayout>
    );
}
